import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Sidebar from '../components/Sidebar';

// ── helpers ────────────────────────────────────────────────────────────────

const token = () => localStorage.getItem('token');

function getUser() {
  try {
    const tok = localStorage.getItem('token');
    if (!tok) return {};
    return JSON.parse(atob(tok.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
  } catch { return {}; }
}

function getReferralStatus(r) {
  if (r.lead_status === 'awaiting_consent')  return 'verifying';
  if (r.lead_status === 'consent_declined')  return 'closed';
  if (r.lead_status === 'consent_expired')   return 'closed';
  if (r.lead_status === 'rejected')          return 'closed';
  if (r.lead_status === 'submitted')         return 'pending';
  if (r.lead_status === 'approved')          return 'active';
  // converted → look at deal stage
  if (r.deal_stage === 'paid')               return 'paid';
  if (r.deal_stage === 'payable')            return 'due';
  if (r.deal_stage === 'cancelled')          return 'closed';
  return 'active'; // pipeline or unknown
}

const fmt = (iso) => iso
  ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
  : '—';

const fmtShort = (iso) => iso
  ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
  : '—';

// ── config ─────────────────────────────────────────────────────────────────

const STATUS_CONFIG = {
  verifying: { label: 'Awaiting Verification', bg: '#fce7f3', color: '#9d174d', border: '#f9a8d4', icon: '📧', dot: '#ec4899' },
  pending:   { label: 'Pending Review',         bg: '#fef3c7', color: '#92400e', border: '#fcd34d', icon: '⏳', dot: '#f59e0b' },
  active:    { label: 'In Progress',            bg: '#dbeafe', color: '#1e40af', border: '#93c5fd', icon: '🔄', dot: '#3b82f6' },
  due:       { label: 'Commission Due',         bg: '#ede9fe', color: '#5b21b6', border: '#c4b5fd', icon: '💰', dot: '#7c3aed' },
  paid:      { label: 'Paid',                   bg: '#d1fae5', color: '#065f46', border: '#6ee7b7', icon: '✅', dot: '#10b981' },
  closed:    { label: 'Closed',                 bg: '#f3f4f6', color: '#6b7280', border: '#d1d5db', icon: '✕',  dot: '#9ca3af' },
};

const CONTACT_LABELS = {
  no_reply:       { label: 'No reply yet',     icon: '📵' },
  call_attempted: { label: 'Call attempted',   icon: '📞' },
  call_made:      { label: 'Spoke to client',  icon: '✅' },
  meeting_booked: { label: 'Meeting booked',   icon: '📅' },
  no_discussion:  { label: 'Unable to contact',icon: '🚫' },
  contacted:      { label: 'Contacted',         icon: '📬' },
};

const RAG = {
  red:   { bg: '#fee2e2', border: '#fca5a5', color: '#991b1b', dot: '#ef4444' },
  amber: { bg: '#fef3c7', border: '#fcd34d', color: '#92400e', dot: '#f59e0b' },
  green: { bg: '#d1fae5', border: '#6ee7b7', color: '#065f46', dot: '#10b981' },
};

const TABS = [
  { key: 'all',       label: 'All',               color: '#0e1b2c' },
  { key: 'verifying', label: 'Awaiting Verify',   color: '#ec4899' },
  { key: 'pending',   label: 'Pending',            color: '#d97706' },
  { key: 'active',    label: 'Active',             color: '#3b82f6' },
  { key: 'due',       label: 'Commission Due',     color: '#7c3aed' },
  { key: 'paid',      label: 'Paid',               color: '#10b981' },
  { key: 'closed',    label: 'Closed',             color: '#9ca3af' },
];

// ── SharedFiles ────────────────────────────────────────────────────────────

function SharedFiles({ dealId }) {
  const [files, setFiles]     = useState(null); // null = loading
  const [error, setError]     = useState('');

  useEffect(() => {
    if (!dealId) { setFiles([]); return; }
    fetch(`/api/introducer/deals/${dealId}/files`, {
      headers: { Authorization: `Bearer ${token()}` },
    })
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(d => setFiles(d.files || []))
      .catch(() => { setError('Could not load files'); setFiles([]); });
  }, [dealId]);

  const download = async (fileId, filename) => {
    try {
      const res = await fetch(`/api/introducer/deals/${dealId}/files/${fileId}/download`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      if (!res.ok) { alert('File not available'); return; }
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
    } catch { alert('Download failed'); }
  };

  const fmtSize = (bytes) => bytes ? (bytes < 1024 * 1024
    ? `${(bytes / 1024).toFixed(0)} KB`
    : `${(bytes / (1024 * 1024)).toFixed(1)} MB`) : '';

  if (!dealId) return (
    <div style={{ fontSize: 13, color: '#a0aec0', fontStyle: 'italic' }}>
      Files are shared once your referral converts to a deal.
    </div>
  );

  if (files === null) return <div style={{ color: '#a0aec0', fontSize: 13 }}>Loading…</div>;
  if (error) return <div style={{ color: '#e53e3e', fontSize: 13 }}>{error}</div>;

  const dip   = files.find(f => f.is_dip_certificate);
  const other = files.filter(f => !f.is_dip_certificate);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>

      {/* DIP banner — always shown, prominent */}
      {dip ? (
        <div style={{
          background: '#d1fae5', border: '2px solid #6ee7b7',
          borderRadius: 10, padding: '14px 16px',
          display: 'flex', alignItems: 'center', gap: 14,
        }}>
          <div style={{
            width: 42, height: 42, borderRadius: 8, background: '#10b981',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 20, flexShrink: 0,
          }}>📄</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#065f46' }}>DIP Certificate Available</div>
            <div style={{ fontSize: 12, color: '#059669', marginTop: 2 }}>
              {dip.original_filename}
              {dip.expires_at && (
                <span style={{ marginLeft: 8, fontWeight: 500 }}>
                  · expires {new Date(dip.expires_at).toLocaleDateString('en-GB')}
                </span>
              )}
            </div>
          </div>
          <button
            onClick={() => download(dip.id, dip.original_filename)}
            style={{
              background: '#0e1b2c', color: '#10b981', border: 'none',
              borderRadius: 8, padding: '9px 18px', fontSize: 13,
              fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap',
              display: 'flex', alignItems: 'center', gap: 6,
            }}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M12 15V3m0 12l-4-4m4 4l4-4M2 17v2a2 2 0 002 2h16a2 2 0 002-2v-2"/>
            </svg>
            Download DIP
          </button>
        </div>
      ) : (
        <div style={{
          background: '#fffbeb', border: '1px solid #fcd34d',
          borderRadius: 8, padding: '11px 14px',
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <span style={{ fontSize: 18 }}>📄</span>
          <div style={{ fontSize: 13, color: '#92400e' }}>
            <strong>No DIP certificate yet.</strong> Your broker will share one here when available.
          </div>
        </div>
      )}

      {/* Other shared files */}
      {other.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 2 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: '#718096', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Other shared files
          </div>
          {other.map(f => (
            <div key={f.id} style={{
              display: 'flex', alignItems: 'center', gap: 10,
              background: '#fff', border: '1px solid #E2E8F0',
              borderRadius: 8, padding: '10px 14px',
            }}>
              <span style={{ fontSize: 16 }}>📎</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#0e1b2c', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {f.original_filename}
                </div>
                <div style={{ fontSize: 11, color: '#a0aec0' }}>
                  {new Date(f.created_at).toLocaleDateString('en-GB')}
                  {fmtSize(f.file_size_bytes) && ` · ${fmtSize(f.file_size_bytes)}`}
                </div>
              </div>
              <button
                onClick={() => download(f.id, f.original_filename)}
                style={{
                  background: 'none', border: '1px solid #CBD5E0',
                  borderRadius: 6, padding: '5px 12px',
                  fontSize: 12, fontWeight: 600, color: '#374151',
                  cursor: 'pointer',
                }}
              >
                Download
              </button>
            </div>
          ))}
        </div>
      )}

      {files.length === 0 && (
        <div style={{ fontSize: 13, color: '#a0aec0', fontStyle: 'italic' }}>
          No files shared by your broker yet.
        </div>
      )}
    </div>
  );
}

// ── MessageThread ──────────────────────────────────────────────────────────

function MessageThread({ leadId, onUnreadCleared }) {
  const [messages, setMessages]   = useState(null); // null = loading
  const [newMsg, setNewMsg]       = useState('');
  const [sending, setSending]     = useState(false);
  const [error, setError]         = useState('');
  const bottomRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`/api/introducer/leads/${leadId}/messages`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setMessages(data.messages || []);
      if (onUnreadCleared) onUnreadCleared(leadId);
    } catch {
      setMessages([]);
    }
  }, [leadId, onUnreadCleared]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (bottomRef.current) bottomRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const send = async () => {
    if (!newMsg.trim() || sending) return;
    setSending(true); setError('');
    try {
      const res = await fetch(`/api/introducer/leads/${leadId}/messages`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: newMsg.trim() }),
      });
      if (!res.ok) throw new Error('Failed to send');
      setNewMsg('');
      load();
    } catch (e) {
      setError(e.message);
    } finally {
      setSending(false);
    }
  };

  const fmtTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const today = new Date();
    const sameDay = d.toDateString() === today.toDateString();
    return sameDay
      ? d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
      : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) + ' ' +
        d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  };

  if (messages === null) return (
    <div style={{ textAlign: 'center', padding: '12px 0', color: '#a0aec0', fontSize: 13 }}>Loading…</div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
      {/* Thread */}
      <div style={{
        maxHeight: 260, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8,
        padding: '10px 0', marginBottom: 10,
      }}>
        {messages.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#a0aec0', fontSize: 13, fontStyle: 'italic', padding: '8px 0' }}>
            No messages yet — send a message to your broker below.
          </div>
        ) : messages.map(m => {
          const isMe = m.sender_role === 'introducer';
          return (
            <div key={m.id} style={{
              display: 'flex', flexDirection: 'column',
              alignItems: isMe ? 'flex-end' : 'flex-start',
            }}>
              <div style={{
                maxWidth: '78%',
                background: isMe ? '#0e1b2c' : '#eff6ff',
                color: isMe ? '#e2e8f0' : '#1e40af',
                borderRadius: isMe ? '12px 12px 2px 12px' : '12px 12px 12px 2px',
                padding: '8px 12px',
                fontSize: 13,
                lineHeight: 1.45,
                wordBreak: 'break-word',
              }}>
                {m.message}
              </div>
              <div style={{ fontSize: 10, color: '#a0aec0', marginTop: 2, paddingLeft: 4, paddingRight: 4 }}>
                {isMe ? 'You' : m.sender_name || 'Broker'} · {fmtTime(m.created_at)}
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      {error && <div style={{ fontSize: 12, color: '#e53e3e', marginBottom: 6 }}>{error}</div>}
      <div style={{ display: 'flex', gap: 8 }}>
        <input
          value={newMsg}
          onChange={e => setNewMsg(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder="Message your broker…"
          style={{
            flex: 1, padding: '8px 12px', borderRadius: 8,
            border: '1px solid #CBD5E0', fontSize: 13, outline: 'none',
          }}
        />
        <button onClick={send} disabled={sending || !newMsg.trim()} style={{
          background: '#0e1b2c', color: '#10b981', border: 'none',
          borderRadius: 8, padding: '8px 16px', fontWeight: 600, fontSize: 13,
          cursor: sending || !newMsg.trim() ? 'not-allowed' : 'pointer',
          opacity: sending || !newMsg.trim() ? 0.5 : 1,
          whiteSpace: 'nowrap',
        }}>
          {sending ? '…' : 'Send'}
        </button>
      </div>
    </div>
  );
}

// ── ReferralCard ───────────────────────────────────────────────────────────

function ReferralCard({ referral: r, expanded, onToggle, showSubmittedBy, unreadCount, onUnreadCleared, onChaseSuccess }) {
  const status = getReferralStatus(r);
  const sc     = STATUS_CONFIG[status];
  const rag    = r.deal_stage_colour ? RAG[r.deal_stage_colour] : null;
  const contact = r.contact_status ? CONTACT_LABELS[r.contact_status] : null;
  const hasBrokerActivity = r.broker_notes || r.contact_status || r.reviewed_by_name;
  const isDeal = !!r.deal_id;

  // Consent chase state
  const [chasing, setChasing] = useState(false);
  const [chaseMsg, setChaseMsg] = useState('');
  const [chaseSentAt, setChaseSentAt] = useState(r.consent_chase_2_at || r.consent_chase_1_at || null);

  // Allow manual chase after 24h from submission or last chase
  const lastActivity = chaseSentAt || r.consent_requested_at;
  const hoursSince = lastActivity ? (Date.now() - new Date(lastActivity).getTime()) / 36e5 : 999;
  const canChase = status === 'verifying' && hoursSince >= 2;

  const sendChase = async (e) => {
    e.stopPropagation();
    setChasing(true); setChaseMsg('');
    try {
      const res = await fetch(`/api/introducer-leads/${r.id}/chase`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token()}` },
      });
      const d = await res.json();
      if (!res.ok) { setChaseMsg(d.error || 'Failed to send'); setChasing(false); return; }
      setChaseMsg('Chase email sent ✓');
      setChaseSentAt(new Date().toISOString());
      if (onChaseSuccess) onChaseSuccess(r.id);
    } catch { setChaseMsg('Network error'); }
    setChasing(false);
  };

  const downloadDip = async (e) => {
    e.stopPropagation();
    try {
      const res = await fetch(`/api/introducer/deals/${r.deal_id}/dip/download`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      if (!res.ok) { alert('DIP certificate not available'); return; }
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href = url; a.download = r.dip_filename || 'dip-certificate.pdf'; a.click();
      URL.revokeObjectURL(url);
    } catch { alert('Download failed'); }
  };

  return (
    <div style={{
      background: '#fff',
      border: `1px solid ${expanded ? sc.border : '#E2E8F0'}`,
      borderLeft: `4px solid ${sc.border}`,
      borderRadius: 10,
      marginBottom: 10,
      overflow: 'hidden',
      boxShadow: expanded ? '0 4px 14px rgba(0,0,0,0.08)' : 'none',
      transition: 'box-shadow 0.15s',
    }}>

      {/* ── Clickable summary ── */}
      <div onClick={onToggle} style={{ padding: '14px 18px', cursor: 'pointer', userSelect: 'none' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 10 }}>

          {/* Left: client info */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ fontWeight: 700, fontSize: 15, color: '#0e1b2c' }}>{r.client_name}</span>
              {hasBrokerActivity && (
                <span style={{ fontSize: 10, background: '#eff6ff', color: '#1e40af', border: '1px solid #93c5fd', borderRadius: 10, padding: '2px 7px', fontWeight: 600 }}>
                  Broker update
                </span>
              )}
              {unreadCount > 0 && (
                <span style={{ fontSize: 10, background: '#ef4444', color: '#fff', borderRadius: 10, padding: '2px 7px', fontWeight: 700 }}>
                  {unreadCount} new message{unreadCount > 1 ? 's' : ''}
                </span>
              )}
            </div>
            <div style={{ fontSize: 12, color: '#718096', marginTop: 2 }}>{r.client_email}</div>
            {showSubmittedBy && r.submitted_by_name && (
              <div style={{ fontSize: 11, color: '#a0aec0', marginTop: 2 }}>Referred by {r.submitted_by_name}</div>
            )}
          </div>

          {/* Right: status badge + commission + chevron */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 5 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 11px', borderRadius: 20, background: sc.bg, color: sc.color, whiteSpace: 'nowrap' }}>
                {sc.icon} {sc.label}
              </span>
              <span style={{ fontSize: 14, color: '#a0aec0', display: 'inline-block', transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}>▾</span>
            </div>
            {r.my_commission != null && parseFloat(r.my_commission) > 0 && (
              <span style={{ fontSize: 13, fontWeight: 700, color: status === 'paid' ? '#10b981' : status === 'due' ? '#7c3aed' : '#718096' }}>
                {status === 'paid' ? '✓ ' : '~'}£{parseFloat(r.my_commission).toLocaleString('en-GB', { minimumFractionDigits: 0 })}
                <span style={{ fontSize: 11, fontWeight: 400, marginLeft: 3 }}>
                  {status === 'paid' ? 'paid' : 'expected'}
                </span>
              </span>
            )}
          </div>
        </div>

        {/* DIP available badge — shown on card without expanding */}
        {r.dip_id && (
          <div style={{ marginTop: 6, display: 'inline-flex', alignItems: 'center', gap: 5,
            background: '#d1fae5', border: '1px solid #6ee7b7', borderRadius: 20,
            padding: '3px 10px', fontSize: 11, fontWeight: 700, color: '#065f46' }}>
            📄 DIP Available
          </div>
        )}

        {/* Progress badge always visible when there's a deal stage */}
        {rag && r.deal_stage_name && (
          <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 11, color: '#718096' }}>Progress:</span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 600,
              padding: '2px 9px', borderRadius: 20, background: rag.bg, border: `1px solid ${rag.border}`, color: rag.color }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: rag.dot, flexShrink: 0 }} />
              {r.deal_stage_name}
            </span>
          </div>
        )}

        <div style={{ marginTop: 5, fontSize: 11, color: '#a0aec0' }}>
          Submitted {fmt(r.created_at)}
          {r.reviewed_at && ` · Reviewed ${fmt(r.reviewed_at)}`}
        </div>
      </div>

      {/* ── Expanded detail panel ── */}
      {expanded && (
        <div style={{ borderTop: '1px solid #EDF2F7', padding: '14px 18px', background: '#fafbfc' }}>

          {/* Your notes */}
          {r.notes && (
            <Section title="Your Notes">
              <TextBox>{r.notes}</TextBox>
            </Section>
          )}

          {/* Broker status update — only while still a Lead. Once converted
              (isDeal), the backend folds this same snapshot into the
              Messages thread below the first time it's opened (6 Aug 2026),
              so it isn't kept here forever as a second, frozen copy next to
              a live conversation. */}
          {!isDeal && (
          <Section title="Broker Status Update">
            {contact && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 8, fontSize: 13, color: '#2d3748' }}>
                <span style={{ fontSize: 15 }}>{contact.icon}</span>
                <span>{contact.label}</span>
                {r.contact_attempts > 1 && <span style={{ fontSize: 11, color: '#a0aec0' }}>({r.contact_attempts} attempts)</span>}
                {r.contact_status_updated_at && <span style={{ fontSize: 11, color: '#a0aec0' }}>· {fmtShort(r.contact_status_updated_at)}</span>}
              </div>
            )}
            {r.broker_notes ? (
              <div style={{ background: '#fff', border: '1px solid #93c5fd', borderLeft: '3px solid #3b82f6', borderRadius: 6, padding: '9px 12px', fontSize: 13, color: '#1e40af' }}>
                <strong style={{ display: 'block', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 3 }}>Broker note</strong>
                {r.broker_notes}
              </div>
            ) : !contact ? (
              <div style={{ fontSize: 13, color: '#a0aec0', fontStyle: 'italic' }}>No update from the broker yet.</div>
            ) : null}
            {r.reviewed_by_name && (
              <div style={{ marginTop: 7, fontSize: 11, color: '#a0aec0' }}>
                Reviewed by {r.reviewed_by_name}{r.reviewed_at && ` · ${fmt(r.reviewed_at)}`}
              </div>
            )}
          </Section>
          )}

          {/* Deal progress (converted leads) */}
          {isDeal && (
            <Section title="Deal Progress">
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 12, marginBottom: rag || r.my_commission != null ? 12 : 0 }}>
                {rag && r.deal_stage_name && (
                  <div>
                    <Label>Current Stage</Label>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 600,
                      padding: '3px 10px', borderRadius: 20, background: rag.bg, border: `1px solid ${rag.border}`, color: rag.color }}>
                      <span style={{ width: 6, height: 6, borderRadius: '50%', background: rag.dot, flexShrink: 0 }} />
                      {r.deal_stage_name}
                    </span>
                  </div>
                )}
                {r.deal_ref && (
                  <div>
                    <Label>Reference</Label>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#0e1b2c' }}>{r.deal_ref}</div>
                  </div>
                )}
                {r.my_commission != null && (
                  <div>
                    <Label>{status === 'paid' ? 'Commission Paid' : status === 'due' ? 'Commission Due' : 'Expected Commission'}</Label>
                    <div style={{ fontSize: 16, fontWeight: 700, color: status === 'paid' ? '#10b981' : status === 'due' ? '#7c3aed' : '#718096' }}>
                      £{parseFloat(r.my_commission).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                  </div>
                )}
              </div>
              {/* Stage colour legend */}
              <div style={{ display: 'flex', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
                {[['green','On track'],['amber','Needs attention'],['red','Action required']].map(([c, label]) => (
                  <span key={c} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, color: RAG[c].color }}>
                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: RAG[c].dot }} /> {label}
                  </span>
                ))}
              </div>
              {r.dip_id && (
                <button onClick={downloadDip} style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  background: '#0e1b2c', color: '#10b981', border: '1px solid #1e3a5f',
                  borderRadius: 6, padding: '7px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
                }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <path d="M12 15V3m0 12l-4-4m4 4l4-4M2 17v2a2 2 0 002 2h16a2 2 0 002-2v-2"/>
                  </svg>
                  Download DIP Certificate
                  {r.dip_expires_at && (
                    <span style={{ fontSize: 10, opacity: 0.7, fontWeight: 400 }}>
                      exp. {fmtShort(r.dip_expires_at)}
                    </span>
                  )}
                </button>
              )}
            </Section>
          )}

          {/* Closed reason */}
          {/* ── Consent verification panel ── */}
          {status === 'verifying' && (
            <div style={{ background: '#fdf2f8', border: '1px solid #f9a8d4', borderRadius: 10, padding: '16px 18px', marginBottom: 14 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <span style={{ fontSize: 18 }}>📧</span>
                <span style={{ fontWeight: 700, fontSize: 14, color: '#9d174d' }}>Awaiting client verification</span>
              </div>
              <p style={{ fontSize: 13, color: '#4b5563', margin: '0 0 12px', lineHeight: 1.6 }}>
                A verification email has been sent to <strong>{r.client_email}</strong>. Your client must click the link to confirm their enquiry before it reaches the broker.
              </p>
              <p style={{ fontSize: 13, color: '#6b7280', margin: '0 0 12px', lineHeight: 1.5 }}>
                💡 <strong>Tip:</strong> Let your client know to check their inbox (including spam) for an email from us.
              </p>

              {/* Timeline */}
              <div style={{ display: 'grid', gap: 6, marginBottom: 14 }}>
                {[
                  { label: 'Verification sent', at: r.consent_requested_at, icon: '📤' },
                  { label: '24h reminder sent',  at: r.consent_chase_1_at,  icon: '⏰' },
                  { label: '48h reminder sent',  at: r.consent_chase_2_at,  icon: '⏰' },
                ].filter(x => x.at).map(x => (
                  <div key={x.label} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#6b7280' }}>
                    <span>{x.icon}</span>
                    <span>{x.label}</span>
                    <span style={{ marginLeft: 'auto', fontWeight: 600 }}>{fmt(x.at)}</span>
                  </div>
                ))}
                {r.consent_expires_at && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#9ca3af' }}>
                    <span>📅</span>
                    <span>Link expires</span>
                    <span style={{ marginLeft: 'auto' }}>{fmt(r.consent_expires_at)}</span>
                  </div>
                )}
              </div>

              {/* Chase button — shows after 24h */}
              {canChase && (
                <button
                  onClick={sendChase}
                  disabled={chasing}
                  style={{
                    background: chasing ? '#e5e7eb' : '#9d174d',
                    color: chasing ? '#6b7280' : '#fff',
                    border: 'none', borderRadius: 8,
                    padding: '9px 18px', fontSize: 13, fontWeight: 700,
                    cursor: chasing ? 'not-allowed' : 'pointer',
                    transition: 'background .15s',
                  }}
                >
                  {chasing ? 'Sending…' : '📨 Chase client'}
                </button>
              )}
              {!canChase && status === 'verifying' && hoursSince < 24 && (
                <div style={{ fontSize: 12, color: '#9d174d', fontStyle: 'italic' }}>
                  A chase button will appear after 24 hours if your client hasn't responded.
                </div>
              )}
              {chaseMsg && (
                <div style={{ marginTop: 8, fontSize: 12, color: chaseMsg.includes('✓') ? '#065f46' : '#991b1b', fontWeight: 600 }}>
                  {chaseMsg}
                </div>
              )}
            </div>
          )}

          {status === 'closed' && r.lead_status === 'consent_declined' && (
            <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 10, padding: '14px 16px', marginBottom: 14, fontSize: 13, color: '#991b1b' }}>
              <strong>Client declined.</strong> {r.client_name} chose not to proceed. You can resubmit if they change their mind.
            </div>
          )}

          {status === 'closed' && r.lead_status === 'consent_expired' && (
            <div style={{ background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 10, padding: '14px 16px', marginBottom: 14, fontSize: 13, color: '#92400e' }}>
              <strong>Verification expired.</strong> {r.client_name} did not respond within the 14-day window. You can resubmit a new lead if appropriate.
            </div>
          )}

                    {status === 'closed' && r.rejection_reason && (
            <div style={{ padding: '9px 12px', background: '#fff5f5', borderLeft: '3px solid #fc8181', borderRadius: 6, fontSize: 13, color: '#c53030', marginBottom: 14 }}>
              <strong>Reason:</strong> {r.rejection_reason}
            </div>
          )}
          {status === 'closed' && !r.rejection_reason && r.lead_status === 'converted' && (
            <div style={{ padding: '9px 12px', background: '#f3f4f6', borderLeft: '3px solid #d1d5db', borderRadius: 6, fontSize: 13, color: '#6b7280', marginBottom: 14 }}>
              This deal was cancelled.
            </div>
          )}

          {/* ── Shared files from broker ── */}
          <Section title="Files from Broker">
            <SharedFiles dealId={r.deal_id} />
          </Section>

          {/* ── Messaging thread ── */}
          <Section title="Messages" last>
            <div style={{
              background: '#fff',
              border: '1px solid #E2E8F0',
              borderRadius: 8,
              padding: '12px 14px',
            }}>
              <MessageThread leadId={r.id} onUnreadCleared={onUnreadCleared} />
            </div>
          </Section>
        </div>
      )}
    </div>
  );
}

// ── tiny layout helpers ────────────────────────────────────────────────────

function Section({ title, children, last }) {
  return (
    <div style={{ marginBottom: last ? 0 : 14 }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: '#718096', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 7 }}>{title}</div>
      {children}
    </div>
  );
}

function Label({ children }) {
  return <div style={{ fontSize: 11, color: '#718096', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600, marginBottom: 4 }}>{children}</div>;
}

function TextBox({ children }) {
  return (
    <div style={{ fontSize: 13, color: '#4a5568', background: '#fff', border: '1px solid #E2E8F0', padding: '9px 12px', borderRadius: 6 }}>
      {children}
    </div>
  );
}

// ── main page ──────────────────────────────────────────────────────────────


function PartnerEarningsPanel() {
  const [data, setData] = React.useState(null);
  React.useEffect(() => {
    fetch('/api/partners/introducer/referrals', { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (d && Array.isArray(d.referrals) && d.referrals.length) setData(d); })
      .catch(() => {});
  }, []);
  if (!data) return null;
  const fmtM = (v) => '£' + (parseFloat(v) || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const shown = data.referrals.slice(0, 5);
  return (
    <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #E2E8F0', padding: 20, marginBottom: 20 }}>
      <div style={{ fontSize: 14, fontWeight: 700, color: '#0e1b2c', marginBottom: 12 }}>🤝 Partner Referral Earnings</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 12, marginBottom: 12 }}>
        <div style={{ background: '#d1fae5', borderRadius: 8, padding: '12px 14px' }}>
          <div style={{ fontSize: 11, color: '#065f46', fontWeight: 600 }}>Paid to you</div>
          <div style={{ fontSize: 18, fontWeight: 800, color: '#065f46' }}>{fmtM(data.totals.earned)}</div>
        </div>
        <div style={{ background: '#ede9fe', borderRadius: 8, padding: '12px 14px' }}>
          <div style={{ fontSize: 11, color: '#5b21b6', fontWeight: 600 }}>Pending</div>
          <div style={{ fontSize: 18, fontWeight: 800, color: '#5b21b6' }}>{fmtM(data.totals.pending)}</div>
        </div>
      </div>
      {shown.map((r, i) => (
        <div key={r.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: i < shown.length - 1 ? '1px solid #f3f4f6' : 'none', fontSize: 13 }}>
          <div>
            <span style={{ fontWeight: 600, color: '#0e1b2c' }}>{r.client_name}</span>
            <span style={{ color: '#718096' }}> → {r.partner_name} ({r.category})</span>
          </div>
          <div style={{ fontWeight: 700, color: r.status === 'paid' ? '#10b981' : '#718096' }}>
            {r.introducer_amount != null ? fmtM(r.introducer_amount) : (r.introducer_share_pct ? r.introducer_share_pct + '% share' : '—')}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function IntroducerReferralsPage() {
  const navigate      = useNavigate();
  const [searchParams] = useSearchParams();
  const user          = getUser();
  const showSubmittedBy = user.introducerLevel === 'company' || user.introducerLevel === 'solo' || user.introducerLevel === 'manager';

  const [referrals, setReferrals]     = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState('');
  const [activeTab, setActiveTab]     = useState('all');
  const [search, setSearch]           = useState('');
  const [expandedId, setExpandedId]   = useState(null);
  const [commSummary, setCommSummary] = useState(null);
  const [showStmt, setShowStmt]       = useState(false);
  const [statement, setStatement]     = useState(null);
  const [stmtFrom, setStmtFrom]       = useState('');
  const [stmtTo, setStmtTo]           = useState('');
  const [stmtLoading, setStmtLoading] = useState(false);
  const [unreadCounts, setUnreadCounts] = useState({});

  const loadUnread = useCallback(async () => {
    try {
      const res = await fetch('/api/introducer/leads/message-counts', {
        headers: { Authorization: `Bearer ${token()}` },
      });
      if (res.ok) { const d = await res.json(); setUnreadCounts(d.counts || {}); }
    } catch {}
  }, []);

  const handleUnreadCleared = useCallback((leadId) => {
    setUnreadCounts(prev => { const next = { ...prev }; delete next[leadId]; return next; });
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [refRes, sumRes] = await Promise.all([
        fetch('/api/introducer/referrals',         { headers: { Authorization: `Bearer ${token()}` } }),
        fetch('/api/introducer/commission-summary', { headers: { Authorization: `Bearer ${token()}` } }),
      ]);
      if (!refRes.ok) throw new Error();
      const data = await refRes.json();
      setReferrals(data.referrals || []);
      if (sumRes.ok) setCommSummary(await sumRes.json());
    } catch {
      setError('Failed to load referrals');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); loadUnread(); }, [load, loadUnread]);

  // Auto-expand a card when arriving via ?open=<id> (e.g. from dashboard or clients page)
  useEffect(() => {
    const openId = searchParams.get('open');
    if (openId && referrals.length > 0) {
      setExpandedId(openId);
      // Scroll to the card after a short render delay
      setTimeout(() => {
        const el = document.getElementById('referral-card-' + openId);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 150);
    }
  }, [searchParams, referrals]);

  const loadStatement = async () => {
    setStmtLoading(true);
    try {
      const p = new URLSearchParams();
      if (stmtFrom) p.set('from', stmtFrom);
      if (stmtTo)   p.set('to',   stmtTo);
      const res = await fetch(`/api/introducer/commission-statement?${p}`, { headers: { Authorization: `Bearer ${token()}` } });
      if (!res.ok) throw new Error();
      setStatement(await res.json());
    } catch { setStatement({ items: [], total: 0 }); }
    finally  { setStmtLoading(false); }
  };

  // Compute status for each referral and attach it for filtering / counts
  const withStatus = referrals.map(r => ({ ...r, _status: getReferralStatus(r) }));

  const counts = TABS.reduce((acc, t) => {
    acc[t.key] = t.key === 'all' ? withStatus.length : withStatus.filter(r => r._status === t.key).length;
    return acc;
  }, {});

  const filtered = withStatus
    .filter(r => activeTab === 'all' || r._status === activeTab)
    .filter(r => !search
      || r.client_name?.toLowerCase().includes(search.toLowerCase())
      || r.client_email?.toLowerCase().includes(search.toLowerCase())
      || r.submitted_by_name?.toLowerCase().includes(search.toLowerCase())
    );

  const hasDealActivity = withStatus.some(r => r.deal_id);

  return (
    <div className="bc-page-layout">
      <Sidebar />
      <main className="main-content">
        <div style={{ maxWidth: 940, margin: '0 auto', padding: '32px 24px' }}>

          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 28, flexWrap: 'wrap', gap: 12 }}>
            <div>
              <h1 style={{ margin: 0, fontSize: 26, color: '#0e1b2c' }}>My Referrals</h1>
              <p style={{ margin: '4px 0 0', color: '#718096', fontSize: 14 }}>
                {referrals.length} referral{referrals.length !== 1 ? 's' : ''} total · tap a card to see details
              </p>
            </div>
            <button onClick={() => navigate('/submit-lead')} style={{
              background: '#10b981', color: '#fff', border: 'none',
              borderRadius: 8, padding: '10px 20px', fontWeight: 600, fontSize: 14, cursor: 'pointer',
            }}>
              + Refer a Client
            </button>
          </div>

          {error && <div style={{ background: '#fee2e2', color: '#991b1b', borderRadius: 8, padding: '12px 16px', marginBottom: 20 }}>{error}</div>}

          {/* Status summary strip */}
          {!loading && referrals.length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: 12, marginBottom: 24 }}>
              {TABS.filter(t => t.key !== 'all' && counts[t.key] > 0).map(t => {
                const sc = STATUS_CONFIG[t.key];
                return (
                  <div key={t.key}
                    onClick={() => { setActiveTab(t.key); setExpandedId(null); }}
                    style={{ background: '#fff', border: `1px solid ${activeTab === t.key ? sc.border : '#E2E8F0'}`,
                      borderRadius: 10, padding: '14px 16px', cursor: 'pointer',
                      boxShadow: activeTab === t.key ? `0 0 0 2px ${sc.border}` : 'none' }}>
                    <div style={{ fontSize: 22, fontWeight: 700, color: sc.color }}>{counts[t.key]}</div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#0e1b2c', marginTop: 2 }}>{t.label}</div>
                  </div>
                );
              })}
            </div>
          )}

          <PartnerEarningsPanel />

          {/* Commission summary cards — only if any deals exist */}
          {hasDealActivity && commSummary && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(155px, 1fr))', gap: 12, marginBottom: 24 }}>
              <SummaryCard
                value={`£${parseFloat(commSummary.paid_total || 0).toLocaleString('en-GB', { minimumFractionDigits: 0 })}`}
                label="Total Paid" sub={commSummary.paid_this_month > 0 ? `£${parseFloat(commSummary.paid_this_month).toLocaleString('en-GB', { minimumFractionDigits: 0 })} this month` : null}
                dark
              />
              <SummaryCard
                value={`£${parseFloat(commSummary.payable_total || 0).toLocaleString('en-GB', { minimumFractionDigits: 0 })}`}
                label="Commission Due" valueColor="#7c3aed"
              />
              <SummaryCard
                value={`~£${parseFloat(commSummary.pipeline_total || commSummary.commission_pipeline || 0).toLocaleString('en-GB', { minimumFractionDigits: 0 })}`}
                label="In Pipeline" valueColor="#718096" sub="expected"
              />
            </div>
          )}

          {/* Commission statement (collapsible) */}
          {hasDealActivity && (
            <div style={{ background: '#f8fafc', border: '1px solid #E2E8F0', borderRadius: 10, padding: '14px 18px', marginBottom: 24 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
                <div>
                  <span style={{ fontSize: 14, fontWeight: 600, color: '#0e1b2c' }}>Commission Statement</span>
                  <span style={{ fontSize: 12, color: '#718096', marginLeft: 8 }}>filter by date range</span>
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                  <input type="date" value={stmtFrom} onChange={e => setStmtFrom(e.target.value)}
                    style={{ border: '1px solid #CBD5E0', borderRadius: 6, padding: '6px 10px', fontSize: 13 }} />
                  <span style={{ color: '#a0aec0', fontSize: 13 }}>to</span>
                  <input type="date" value={stmtTo} onChange={e => setStmtTo(e.target.value)}
                    style={{ border: '1px solid #CBD5E0', borderRadius: 6, padding: '6px 10px', fontSize: 13 }} />
                  <button onClick={() => { setShowStmt(true); loadStatement(); }} disabled={stmtLoading}
                    style={{ background: '#0e1b2c', color: '#10b981', border: 'none', borderRadius: 6, padding: '7px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                    {stmtLoading ? 'Loading…' : 'View Statement'}
                  </button>
                  {showStmt && statement && (
                    <button onClick={() => window.print()}
                      style={{ background: '#fff', color: '#374151', border: '1px solid #CBD5E0', borderRadius: 6, padding: '7px 14px', fontSize: 13, cursor: 'pointer' }}>
                      🖨 Print
                    </button>
                  )}
                </div>
              </div>
              {showStmt && statement && (
                <div style={{ marginTop: 16, overflowX: 'auto' }}>
                  {statement.items.length === 0 ? (
                    <p style={{ color: '#718096', fontSize: 13, margin: 0 }}>No paid commissions in this period.</p>
                  ) : (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                      <thead>
                        <tr style={{ background: '#EDF2F7' }}>
                          {['Date Paid', 'Client', 'Reference', 'Broker', 'Amount'].map(h => (
                            <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: '#4a5568', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {statement.items.map((item, i) => (
                          <tr key={item.id} style={{ borderBottom: '1px solid #EDF2F7', background: i % 2 === 0 ? '#fff' : '#f8fafc' }}>
                            <td style={{ padding: '9px 12px', color: '#4a5568' }}>{item.paid_at ? fmt(item.paid_at) : '—'}</td>
                            <td style={{ padding: '9px 12px', fontWeight: 600, color: '#0e1b2c' }}>{item.client_name}</td>
                            <td style={{ padding: '9px 12px', color: '#718096' }}>{item.deal_ref || '—'}</td>
                            <td style={{ padding: '9px 12px', color: '#718096' }}>{item.broker_name || '—'}</td>
                            <td style={{ padding: '9px 12px', fontWeight: 700, color: '#10b981' }}>
                              £{parseFloat(item.amount).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr style={{ background: '#0e1b2c' }}>
                          <td colSpan={4} style={{ padding: '9px 12px', fontWeight: 600, color: '#fff', fontSize: 13 }}>
                            Total ({statement.items.length} payment{statement.items.length !== 1 ? 's' : ''})
                          </td>
                          <td style={{ padding: '9px 12px', fontWeight: 700, color: '#10b981', fontSize: 15 }}>
                            £{parseFloat(statement.total).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Search */}
          <input
            value={search} onChange={e => { setSearch(e.target.value); setExpandedId(null); }}
            placeholder="Search by client name, email or adviser…"
            style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #E2E8F0', fontSize: 14, marginBottom: 18, boxSizing: 'border-box' }}
          />

          {/* Tabs */}
          <div style={{ display: 'flex', borderBottom: '2px solid #EDF2F7', marginBottom: 20, flexWrap: 'wrap' }}>
            {TABS.map(tab => (
              (counts[tab.key] > 0 || tab.key === 'all') ? (
                <button key={tab.key}
                  onClick={() => { setActiveTab(tab.key); setExpandedId(null); }}
                  style={{
                    background: 'none', border: 'none',
                    borderBottom: activeTab === tab.key ? `3px solid ${tab.color}` : '3px solid transparent',
                    marginBottom: -2, padding: '8px 14px', fontSize: 13,
                    fontWeight: activeTab === tab.key ? 600 : 500,
                    color: activeTab === tab.key ? tab.color : '#718096',
                    cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
                  }}>
                  {tab.label}
                  {counts[tab.key] > 0 && (
                    <span style={{ background: tab.color, color: '#fff', fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 10 }}>
                      {counts[tab.key]}
                    </span>
                  )}
                </button>
              ) : null
            ))}
          </div>

          {/* Cards */}
          {loading ? (
            <div style={{ textAlign: 'center', padding: 48, color: '#718096' }}>Loading…</div>
          ) : filtered.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 48, color: '#718096' }}>
              {referrals.length === 0 ? (
                <>
                  <p style={{ marginBottom: 16 }}>No referrals yet.</p>
                  <button onClick={() => navigate('/submit-lead')} style={{
                    background: '#10b981', color: '#fff', border: 'none',
                    borderRadius: 8, padding: '10px 20px', fontWeight: 600, cursor: 'pointer',
                  }}>Refer your first client</button>
                </>
              ) : (
                <p>No {activeTab === 'all' ? '' : activeTab + ' '}referrals{search ? ' matching your search' : ''}.</p>
              )}
            </div>
          ) : (
            filtered.map(r => (
              <ReferralCard
                key={r.id}
                referral={r}
                expanded={expandedId === r.id}
                onToggle={() => setExpandedId(prev => prev === r.id ? null : r.id)}
                showSubmittedBy={showSubmittedBy}
                unreadCount={unreadCounts[r.id] || 0}
                onUnreadCleared={handleUnreadCleared}
                onChaseSuccess={() => {}}
              />
            ))
          )}
        </div>
      </main>
    </div>
  );
}

function SummaryCard({ value, label, sub, dark, valueColor }) {
  return (
    <div style={{
      background: dark ? '#0e1b2c' : '#fff',
      border: '1px solid #E2E8F0',
      borderRadius: 10,
      padding: '16px 18px',
    }}>
      <div style={{ fontSize: 22, fontWeight: 700, color: dark ? '#10b981' : (valueColor || '#0e1b2c') }}>{value}</div>
      <div style={{ fontSize: 12, fontWeight: 600, color: dark ? '#fff' : '#0e1b2c', marginTop: 3 }}>{label}</div>
      {sub && <div style={{ fontSize: 11, color: dark ? '#94a3b8' : '#a0aec0', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}
